import { createContext, useContext, useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getToken, removeToken, isAuthenticated } from "../services/api";
import {
  initializeAuth,
  logout as logoutAction,
  setAuthFromContext,
} from "../store/slices/authSlice";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get Redux auth state
  const reduxAuth = useSelector((state) => state.auth);

  // Initialize on mount
  useEffect(() => {
    const checkAuth = async () => {
      // Initialize Redux auth state
      await dispatch(initializeAuth());

      // Use isAuthenticated helper for additional validation
      if (isAuthenticated()) {
        const token = getToken();
        setUser({ token });

        // Sync with Redux
        dispatch(setAuthFromContext({ token }));
      }
      setIsLoading(false);
    };

    checkAuth();
  }, [dispatch]);

  // Sync with Redux logout - when auth is cleared, clear context
  useEffect(() => {
    if (!reduxAuth.isAuthenticated && user) {
      // Redux logged out, sync context
      setUser(null);
    }
  }, [reduxAuth.isAuthenticated]);

  // Sync with Redux login - when new user logs in
  useEffect(() => {
    if (reduxAuth.isAuthenticated && reduxAuth.token && !user) {
      // Redux logged in, sync context
      setUser({ token: reduxAuth.token });
    }
  }, [reduxAuth.isAuthenticated, reduxAuth.token]);

  const login = (token) => {
    setUser({ token });
    // Redux state is already updated by loginUser action
  };

  const logout = () => {
    removeToken(); // This already clears currentUser, isFirstTime, expired, username
    setUser(null);

    // Sync with Redux
    dispatch(logoutAction());
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
};